import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Users,
  UserPlus,
  Building2,
  Rocket,
  Folder,
  CalendarDays,
  Inbox,
  TrendingUp,
  AlertTriangle,
  Activity,
  ClipboardList,
  GraduationCap,
  Compass,
  Radio,
  Footprints,
  History,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { Sparkline } from "@/components/dashboard/Sparkline";
import { CarouselRow } from "@/components/admin/CarouselRow";

function fmtNumber(n) {
  return (n ?? 0).toLocaleString();
}
function fmtRelative(iso, tr) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return tr ? tr("adminOverview.relJustNow") : "just now";
  if (diff < 3600) return tr ? tr("adminOverview.relMinutesAgo", { n: Math.floor(diff / 60) }) : `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return tr ? tr("adminOverview.relHoursAgo", { n: Math.floor(diff / 3600) }) : `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return tr ? tr("adminOverview.relDaysAgo", { n: Math.floor(diff / 86400) }) : `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

/**
 * Translate a raw backend enum key using an i18n namespace, falling back
 * to a humanized version of the key itself so unknown values don't leak
 * as a translation-missing placeholder.
 */
function localizeKey(tr, namespace, key) {
  if (!key) return "";
  const path = `adminOverview.${namespace}.${key}`;
  const translated = tr(path);
  if (translated && translated !== path) return translated;
  return String(key).replace(/_/g, " ");
}

function StatTile({ icon: Icon, label, value, sub, tone = "default", testId, href, onNavigate, openLabel }) {
  const toneMap = {
    default: "bg-primary/10 text-primary",
    warn: "bg-amber-500/10 text-amber-600",
    danger: "bg-rose-500/10 text-rose-600",
    success: "bg-emerald-500/10 text-emerald-600",
  };
  const clickable = !!(href || onNavigate);
  return (
    <Card
      className={`h-full rounded-2xl border border-border p-3 sm:p-5 transition-all ${clickable ? "cursor-pointer hover:border-primary/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40" : ""}`}
      onClick={clickable ? (onNavigate || (() => window.location.assign(href))) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); (onNavigate || (() => window.location.assign(href)))(); } } : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `${openLabel || "Open"} ${label}` : undefined}
      data-testid={testId}
    >
      <div className="flex items-start gap-2.5 sm:gap-3">
        <span className={`flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl flex-shrink-0 ${toneMap[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</p>
          <p className="font-display text-xl sm:text-2xl font-semibold mt-0.5 sm:mt-1" dir="ltr">{value}</p>
          {sub && <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function TrendCard({ label, series, tone = "default", testId, last7Label }) {
  const values = (series || []).map((p) => p.count);
  const total = values.reduce((a, b) => a + b, 0);
  // Compare the most-recent 7-day window vs the prior 7-day window
  // so the % delta reflects momentum instead of just today's count.
  const window = 7;
  const recent = values.slice(-window).reduce((a, b) => a + b, 0);
  const prev = values.slice(-window * 2, -window).reduce((a, b) => a + b, 0);
  const delta = prev ? Math.round(((recent - prev) / Math.abs(prev)) * 100) : (recent ? 100 : 0);
  return (
    <Card className="h-full rounded-2xl border border-border p-3 sm:p-5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <TrendingUp className="h-3.5 w-3.5 text-primary shrink-0" />
        <p className="text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground font-medium truncate">{label}</p>
        <span className="ms-auto text-[10px] font-mono text-muted-foreground shrink-0" dir="ltr">{fmtNumber(recent)} {last7Label}</span>
      </div>
      <div className="flex items-baseline justify-between mt-2">
        <span className="font-mono text-xl sm:text-2xl font-semibold" dir="ltr">{fmtNumber(total)}</span>
        <span className={`text-xs font-mono ${delta >= 0 ? "text-emerald-500" : "text-rose-500"}`} dir="ltr">
          {delta > 0 ? "+" : ""}{delta}%
        </span>
      </div>
      <div className="mt-2"><Sparkline values={values} width={260} height={40} /></div>
    </Card>
  );
}

function DistributionCard({ label, items, testId, hrefFor, emptyLabel, keyNamespace, tr }) {
  const total = items.reduce((a, b) => a + b.count, 0) || 1;
  return (
    <Card className="h-full rounded-2xl border border-border p-3 sm:p-5" data-testid={testId}>
      <p className="text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground font-medium">{label}</p>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-3">{emptyLabel}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((r) => {
            const pct = Math.round((r.count / total) * 100);
            const href = hrefFor ? hrefFor(r) : null;
            const RowTag = href ? "button" : "div";
            const rowLabel = keyNamespace && tr
              ? localizeKey(tr, keyNamespace, r.key)
              : (r.key || "unknown").replace(/_/g, " ");
            return (
              <li key={r.key} data-testid={`${testId}-row-${r.key}`}>
                <RowTag
                  {...(href ? { type: "button", onClick: () => window.location.assign(href) } : {})}
                  className={`w-full space-y-1 text-left ${href ? "cursor-pointer group focus:outline-none" : ""}`}
                >
                  <div className="flex items-center justify-between text-xs">
                    <span className={`truncate capitalize ${href ? "group-hover:text-primary" : ""}`}>{rowLabel}</span>
                    <span className="font-mono text-muted-foreground" dir="ltr">{fmtNumber(r.count)} · {pct}%</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div className={`h-full transition-all ${href ? "bg-primary group-hover:bg-primary/80" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                  </div>
                </RowTag>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export function AdminOverview() {
  const navigate = useNavigate();
  const { t: tr, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  // Journey business types are admin-authored records, so their labels come
  // from the API rather than the i18n bundle.
  const [typeLabels, setTypeLabels] = useState({});
  // Latest-tr ref so the memoised load() can call it without needing
  // to depend on the changing t function identity.
  const trRef = useRef(tr);
  useEffect(() => { trRef.current = tr; }, [tr]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [journey, setJourney] = useState(null);
  const [journeySessions, setJourneySessions] = useState([]);
  const [journeyFilter, setJourneyFilter] = useState("converted");
  const [journeyLoading, setJourneyLoading] = useState(false);
  // The journey block has two long tails (type breakdown + session list) that
  // dominate a phone screen. Collapsed by default under `sm`.
  const [journeyOpen, setJourneyOpen] = useState(false);
  const typeLabel = useCallback(
    (slug) => typeLabels[slug] || String(slug || "unknown").replace(/-/g, " "),
    [typeLabels],
  );

  const go = useCallback((path) => () => navigate(path), [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch overview + journey analytics in parallel; a journey
      // failure should not block the primary overview view.
      const [ov, jr] = await Promise.all([
        client.get("/admin/overview"),
        client.get("/admin/journey/analytics").catch(() => ({ data: null })),
      ]);
      setData(ov.data);
      setJourney(jr.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || trRef.current("adminOverview.loadFailed");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadJourneySessions = useCallback(async (filter) => {
    setJourneyLoading(true);
    try {
      const res = await client.get(`/admin/journey/sessions?status=${filter}&limit=50`);
      setJourneySessions(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || trRef.current("adminOverview.loadFailed"));
    } finally {
      setJourneyLoading(false);
    }
  }, []);
  useEffect(() => { loadJourneySessions(journeyFilter); }, [loadJourneySessions, journeyFilter]);

  useEffect(() => {
    client.get("/admin/business-types")
      .then((res) => {
        const map = {};
        (res.data?.items || []).forEach((b) => {
          map[b.slug] = (isAr ? b.name_ar : b.name_en) || b.slug;
        });
        setTypeLabels(map);
      })
      .catch(() => {});
  }, [isAr]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center" data-testid="admin-overview-error">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-600">
          <AlertTriangle className="h-6 w-6" />
        </span>
        <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
        <button
          type="button"
          onClick={load}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors"
          data-testid="admin-overview-retry"
        >
          {tr("adminOverview.retry")}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const u = data.users || {};
  const w = data.waitlist || {};
  const co = data.companies || {};
  const dr = data.data_rooms || {};
  const p = data.programs || {};
  const ev = data.events || {};
  const c = data.contact || {};
  const eco = data.ecosystem || {};
  const vis = data.visitors || {};
  const t = data.trends_30d || {};

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Activity className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-semibold">{tr("adminOverview.pageTitle")}</h2>
          <p className="text-sm text-muted-foreground">
            {tr("adminOverview.pageSubtitle")}
          </p>
        </div>
        <span className="text-[11px] text-muted-foreground font-mono" data-testid="admin-overview-generated-at">
          {tr("adminOverview.refreshed")} {fmtRelative(data.generated_at, tr)}
        </span>
      </div>

      {/* Visitor pulse — one compact 3-up card on phones instead of three
          stacked cards, still drilling into journeys / change log. */}
      <div className="grid grid-cols-3 gap-0 sm:gap-3 rounded-2xl border border-border overflow-hidden sm:border-0 sm:rounded-none" data-testid="admin-overview-visitor-pulse">
        <button
          type="button"
          onClick={go("/admin?section=sessions")}
          className="bg-card p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 text-start transition-all border-e border-border last:border-e-0 sm:border sm:rounded-2xl sm:border-border hover:border-primary/50 sm:hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="admin-overview-live-visitors"
        >
          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
            <Radio className="h-4 w-4" />
            <span className="absolute -top-0.5 -end-0.5 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </span>
          </span>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">
              {tr("adminOverview.liveVisitors", { defaultValue: "Live visitors" })}
            </p>
            <p className="font-mono text-base sm:text-lg" dir="ltr">{fmtNumber(vis.live)}</p>
          </div>
        </button>
        <button
          type="button"
          onClick={go("/admin?section=sessions")}
          className="bg-card p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 text-start transition-all border-e border-border last:border-e-0 sm:border sm:rounded-2xl sm:border-border hover:border-primary/50 sm:hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="admin-overview-sessions-24h"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Footprints className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">
              {tr("adminOverview.sessions24h", { defaultValue: "Sessions · 24h" })}
            </p>
            <p className="font-mono text-base sm:text-lg" dir="ltr">{fmtNumber(vis.sessions_24h)}</p>
          </div>
        </button>
        <button
          type="button"
          onClick={go("/admin?section=activity")}
          className="bg-card p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 text-start transition-all border-e border-border last:border-e-0 sm:border sm:rounded-2xl sm:border-border hover:border-primary/50 sm:hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="admin-overview-changes-24h"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600"><History className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">
              {tr("adminOverview.convertedToday", { defaultValue: "Converted · 24h" })}
            </p>
            <p className="font-mono text-base sm:text-lg" dir="ltr">{fmtNumber(vis.converted_24h)}</p>
          </div>
        </button>
      </div>

      {/* Row 1 — headline stats (swipeable on phones) */}
      <CarouselRow testId="admin-overview-row-headline">
        <StatTile
          icon={Users}
          label={tr("adminOverview.statTotalUsers")}
          value={fmtNumber(u.total)}
          sub={tr("adminOverview.statTotalUsersSub", { d7: fmtNumber(u.new_last_7d), d30: fmtNumber(u.new_last_30d) })}
          testId="admin-overview-stat-users"
          onNavigate={go("/admin?section=users")}
          openLabel={tr("adminOverview.openLabel")}
        />
        <StatTile
          icon={UserPlus}
          label={tr("adminOverview.statWaitlist")}
          value={fmtNumber(w.total)}
          sub={(w.by_status || []).map((r) => `${r.count} ${localizeKey(tr, "waitlistStatus", r.key)}`).slice(0, 3).join(" · ") || tr("adminOverview.statWaitlistEmpty")}
          tone="success"
          testId="admin-overview-stat-waitlist"
          onNavigate={go("/admin?section=waitlist")}
          openLabel={tr("adminOverview.openLabel")}
        />
        <StatTile
          icon={Building2}
          label={tr("adminOverview.statCompanies")}
          value={fmtNumber(co.total)}
          sub={tr("adminOverview.statCompaniesSub", { enrolled: fmtNumber(co.enrolled), graduated: fmtNumber(co.graduated) })}
          testId="admin-overview-stat-companies"
          onNavigate={go("/admin?section=companies")}
          openLabel={tr("adminOverview.openLabel")}
        />
        <StatTile
          icon={Folder}
          label={tr("adminOverview.statDataRooms")}
          value={fmtNumber(dr.total)}
          sub={(dr.by_stage || []).map((r) => `${r.count} ${localizeKey(tr, "roomStage", r.key)}`).slice(0, 2).join(" · ") || tr("adminOverview.statDataRoomsEmpty")}
          testId="admin-overview-stat-datarooms"
          onNavigate={go("/admin?section=data_room")}
          openLabel={tr("adminOverview.openLabel")}
        />
      </CarouselRow>

      {/* Row 2 — program + events + engagement */}
      <CarouselRow testId="admin-overview-row-operations">
        <StatTile
          icon={Rocket}
          label={tr("adminOverview.statPrograms")}
          value={fmtNumber(p.total)}
          sub={tr("adminOverview.statProgramsSub", { pct: p.completion_pct })}
          testId="admin-overview-stat-programs"
          onNavigate={go("/admin?section=programs")}
          openLabel={tr("adminOverview.openLabel")}
        />
        <StatTile
          icon={AlertTriangle}
          label={tr("adminOverview.statOverdue")}
          value={fmtNumber(p.milestones_overdue)}
          sub={tr("adminOverview.statOverdueSub", { total: fmtNumber(p.milestones_total) })}
          tone={p.milestones_overdue > 0 ? "danger" : "success"}
          testId="admin-overview-stat-overdue"
          onNavigate={go("/admin?section=companies&overdue=1")}
          openLabel={tr("adminOverview.openLabel")}
        />
        <StatTile
          icon={CalendarDays}
          label={tr("adminOverview.statEvents")}
          value={fmtNumber(ev.total)}
          sub={tr("adminOverview.statEventsSub", { upcoming: fmtNumber(ev.upcoming), rsvps: fmtNumber(ev.rsvps_total) })}
          testId="admin-overview-stat-events"
        />
        <StatTile
          icon={Inbox}
          label={tr("adminOverview.statContact")}
          value={fmtNumber(c.total)}
          sub={tr("adminOverview.statContactSub", { n: fmtNumber(c.new_last_7d) })}
          tone={c.new_last_7d > 0 ? "warn" : "default"}
          testId="admin-overview-stat-contact"
          onNavigate={go("/admin?section=contact")}
          openLabel={tr("adminOverview.openLabel")}
        />
      </CarouselRow>

      {/* Row 3 — 30d trend sparklines */}
      <CarouselRow testId="admin-overview-row-trends" itemWidth="w-[80%]">
        <TrendCard label={tr("adminOverview.trendSignups")} series={t.signups} testId="admin-overview-trend-signups" last7Label={tr("adminOverview.trendLast7")} />
        <TrendCard label={tr("adminOverview.trendWaitlist")} series={t.waitlist} testId="admin-overview-trend-waitlist" last7Label={tr("adminOverview.trendLast7")} />
        <TrendCard label={tr("adminOverview.trendContact")} series={t.contact} testId="admin-overview-trend-contact" last7Label={tr("adminOverview.trendLast7")} />
        <TrendCard label={tr("adminOverview.trendEventRsvps")} series={t.rsvps} testId="admin-overview-trend-rsvps" last7Label={tr("adminOverview.trendLast7")} />
      </CarouselRow>

      {/* Journey funnel — anonymous sessions → converted signups.
          Rendered as its own row so drop-off is easy to spot at a glance. */}
      {journey && (
        <Card
          className="rounded-2xl border border-border p-4 sm:p-5"
          data-testid="admin-overview-journey"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Compass className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                {tr("adminOverview.journeyTitle")}
              </p>
              <p className="text-sm text-muted-foreground">
                {tr("adminOverview.journeySubtitle")}
              </p>
            </div>
            <button
              type="button"
              onClick={go("/admin?section=journey_types")}
              className="ml-auto text-[10px] uppercase tracking-widest text-primary hover:underline"
              data-testid="admin-overview-journey-viewall"
            >
              {tr("adminOverview.journeyManage")} →
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4">
            <div className="rounded-xl border border-border p-2.5 sm:p-3" data-testid="admin-overview-journey-sessions">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {tr("adminOverview.journeySessions")}
              </p>
              <p className="font-display text-xl sm:text-2xl font-semibold mt-1" dir="ltr">
                {fmtNumber(journey.total_sessions)}
              </p>
            </div>
            <div className="rounded-xl border border-border p-2.5 sm:p-3" data-testid="admin-overview-journey-converted">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {tr("adminOverview.journeyConverted")}
              </p>
              <p className="font-display text-xl sm:text-2xl font-semibold mt-1 text-emerald-600" dir="ltr">
                {fmtNumber(journey.converted)}
              </p>
              <p className="text-[11px] font-mono text-muted-foreground" dir="ltr">
                {journey.conversion_rate_pct}% {tr("adminOverview.journeyConversionSuffix")}
              </p>
            </div>
            <div className="rounded-xl border border-border p-2.5 sm:p-3" data-testid="admin-overview-journey-dropped">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {tr("adminOverview.journeyDropped")}
              </p>
              <p className="font-display text-xl sm:text-2xl font-semibold mt-1 text-rose-600" dir="ltr">
                {fmtNumber(journey.dropped_off)}
              </p>
              <p className="text-[11px] font-mono text-muted-foreground" dir="ltr">
                {journey.dropoff_rate_pct}% {tr("adminOverview.journeyDropoffSuffix")}
              </p>
            </div>
            <div className="rounded-xl border border-border p-2.5 sm:p-3" data-testid="admin-overview-journey-types">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {tr("adminOverview.journeyTypes")}
              </p>
              <p className="font-display text-xl sm:text-2xl font-semibold mt-1" dir="ltr">
                {Object.keys(journey.by_business_type || {}).length}
              </p>
              <p className="text-[11px] font-mono text-muted-foreground">
                {tr("adminOverview.journeyTypesHint")}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setJourneyOpen((v) => !v)}
            className="sm:hidden mt-3 w-full rounded-xl border border-border py-2 text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            data-testid="admin-overview-journey-toggle"
          >
            {journeyOpen ? tr("adminOverview.journeyHideDetails") : tr("adminOverview.journeyShowDetails")}
          </button>

          {Object.keys(journey.by_business_type || {}).length > 0 && (
            <div className={`mt-4 ${journeyOpen ? "" : "hidden sm:block"}`}>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">
                {tr("adminOverview.journeyTopTypes")}
              </p>
              <ul className="space-y-2">
                {Object.entries(journey.by_business_type)
                  .map(([slug, v]) => ({
                    slug,
                    sessions: v.sessions || 0,
                    converted: v.converted || 0,
                  }))
                  .sort((a, b) => b.sessions - a.sessions)
                  .slice(0, 5)
                  .map((row) => {
                    const rate = row.sessions ? Math.round((row.converted / row.sessions) * 100) : 0;
                    return (
                      <li
                        key={row.slug}
                        className="grid grid-cols-[1fr_auto] gap-x-3 items-center"
                        data-testid={`admin-overview-journey-type-${row.slug}`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center justify-between text-xs">
                            <span className="truncate">{typeLabel(row.slug)}</span>
                            <span className="font-mono text-muted-foreground" dir="ltr">
                              {fmtNumber(row.converted)} / {fmtNumber(row.sessions)} · {rate}%
                            </span>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden mt-1">
                            <div
                              className="h-full bg-primary"
                              style={{ width: `${Math.min(rate, 100)}%` }}
                            />
                          </div>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}

          {/* Sessions drill-down — see which visitors converted and to
              which user account. Filter switches between converted /
              dropped / all so support can triage hot leads. */}
          <div className={`mt-6 pt-4 border-t border-border ${journeyOpen ? "" : "hidden sm:block"}`}>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
                {tr("adminOverview.journeySessionsTitle")}
              </p>
              <div className="ml-auto flex items-center gap-1 rounded-lg border border-border p-1 text-[10px] font-medium">
                {["converted", "dropped", "all"].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setJourneyFilter(f)}
                    className={`px-2 py-1 rounded-md transition-colors ${journeyFilter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`admin-overview-journey-filter-${f}`}
                  >
                    {tr(`adminOverview.journeyFilter.${f}`)}
                  </button>
                ))}
              </div>
            </div>
            {journeyLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
            ) : journeySessions.length === 0 ? (
              <p className="text-xs text-muted-foreground py-3 text-center" data-testid="admin-overview-journey-sessions-empty">
                {tr("adminOverview.journeySessionsEmpty")}
              </p>
            ) : (
              <ul className="divide-y divide-border max-h-[320px] overflow-y-auto" data-testid="admin-overview-journey-sessions-list">
                {journeySessions.map((s) => (
                  <li
                    key={s.session_id}
                    className="py-2.5 grid grid-cols-[1fr_auto] gap-x-3 items-center"
                    data-testid={`admin-overview-journey-session-${s.session_id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        {s.converted_user_email ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin?section=users&q=${encodeURIComponent(s.converted_user_email)}`)}
                            className="font-medium truncate hover:text-primary hover:underline text-left"
                            title={s.converted_user_email}
                            data-testid={`admin-overview-journey-user-${s.session_id}`}
                          >
                            {s.converted_user_name
                              ? `${s.converted_user_name} · ${s.converted_user_email}`
                              : s.converted_user_email}
                            {s.converted_user_status && s.converted_user_status !== "approved" && (
                              <span className="ms-2 inline-flex h-4 items-center rounded-full bg-amber-500/15 text-amber-700 px-1.5 text-[9px] uppercase tracking-widest">
                                {s.converted_user_status}
                              </span>
                            )}
                          </button>
                        ) : (
                          <span className="font-mono text-[10px] text-muted-foreground truncate">
                            {tr("adminOverview.journeyAnon")} · {s.session_id.slice(0, 12)}
                          </span>
                        )}
                        <span className="capitalize text-muted-foreground">·</span>
                        <span className="text-muted-foreground truncate">{typeLabel(s.business_type_slug)}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono mt-0.5" dir="ltr">
                        {s.converted_at
                          ? `${tr("adminOverview.journeyConvertedAt")} ${fmtRelative(s.converted_at, tr)}`
                          : `${tr("adminOverview.journeyLastSeen")} ${fmtRelative(s.last_seen_at, tr)}`}
                        {" · "}{s.event_count} {tr("adminOverview.journeyEvents")}
                      </p>
                    </div>
                    <span
                      className={`inline-flex h-6 items-center rounded-full px-2 text-[10px] font-semibold uppercase tracking-widest ${
                        s.converted_at
                          ? "bg-emerald-500/10 text-emerald-700"
                          : "bg-rose-500/10 text-rose-600"
                      }`}
                    >
                      {s.converted_at ? tr("adminOverview.journeyBadgeConverted") : tr("adminOverview.journeyBadgeDropped")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      )}

      {/* Row 4 — distributions + recent activity */}
      <CarouselRow testId="admin-overview-row-breakdown" gridClass="sm:grid-cols-2 lg:grid-cols-3" itemWidth="w-[85%]">
        <DistributionCard
          label={tr("adminOverview.distUsersByRole")}
          items={u.by_role || []}
          testId="admin-overview-users-by-role"
          hrefFor={(r) => `/admin?section=users&role=${encodeURIComponent(r.key)}`}
          emptyLabel={tr("adminOverview.distNoData")}
          keyNamespace="roleLabels"
          tr={tr}
        />
        <DistributionCard
          label={tr("adminOverview.distRoomsByStage")}
          items={dr.by_stage || []}
          testId="admin-overview-rooms-by-stage"
          hrefFor={(r) => `/admin?section=data_room&stage=${encodeURIComponent(r.key)}`}
          emptyLabel={tr("adminOverview.distNoData")}
          keyNamespace="roomStage"
          tr={tr}
        />

        <Card className="rounded-2xl border border-border p-4 sm:p-5" data-testid="admin-overview-recent-activity">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-3.5 w-3.5 text-primary" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
              {tr("adminOverview.changesTitle", { defaultValue: "Latest changes" })}
            </p>
            <button
              type="button"
              onClick={go("/admin?section=activity")}
              className="ms-auto text-[10px] uppercase tracking-widest text-primary hover:underline"
              data-testid="admin-overview-activity-viewall"
            >
              {tr("adminOverview.activityViewAll")} →
            </button>
          </div>
          {(data.recent_changes || []).length === 0 ? (
            <p className="text-sm text-muted-foreground mt-3">{tr("adminOverview.activityEmpty")}</p>
          ) : (
            <ul className="mt-3 space-y-2.5 max-h-[280px] overflow-y-auto pe-1">
              {(data.recent_changes || []).map((r) => (
                <li key={r.audit_id} className="text-xs border-s-2 border-primary/30 ps-2">
                  <p className="truncate">
                    <span className="font-medium">{r.actor_name}</span>{" "}
                    <span className="capitalize text-muted-foreground">{String(r.action).replace(/[._]/g, " ")}</span>
                  </p>
                  <p className="text-muted-foreground truncate">
                    {r.entity_label || r.entity_type} · {fmtRelative(r.created_at, tr)}
                  </p>
                  {(r.changes || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {r.changes.map((c, i) => (
                        <span key={`${c.field}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-1.5 py-0.5 text-[10px] font-mono">
                          {c.field}
                          <ArrowRight className="h-2.5 w-2.5 text-primary rtl-flip" />
                          <span className="font-semibold truncate max-w-[90px]">{String(c.to ?? "—")}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </CarouselRow>

      {/* Row 5 — ecosystem callout strip: one 3-up card on phones */}
      <div className="grid grid-cols-3 gap-0 sm:gap-3 rounded-2xl border border-border overflow-hidden sm:border-0 sm:rounded-none">
        <button
          type="button"
          onClick={go("/admin?section=users")}
          className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 text-start cursor-pointer border-e border-border last:border-e-0 sm:border sm:rounded-2xl sm:border-border hover:border-primary/50 sm:hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="admin-overview-ecosystem-profiles"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0"><Users className="h-4 w-4" /></span>
          <div className="min-w-0"><p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">{tr("adminOverview.ecosystemProfiles")}</p><p className="font-mono text-base sm:text-lg" dir="ltr">{fmtNumber(eco.profiles)}</p></div>
        </button>
        <button
          type="button"
          onClick={go("/admin?section=companies&graduated=1")}
          className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 text-start cursor-pointer border-e border-border last:border-e-0 sm:border sm:rounded-2xl sm:border-border hover:border-primary/50 sm:hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="admin-overview-graduated"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 flex-shrink-0"><GraduationCap className="h-4 w-4" /></span>
          <div className="min-w-0"><p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">{tr("adminOverview.graduatedCompanies")}</p><p className="font-mono text-base sm:text-lg" dir="ltr">{fmtNumber(co.graduated)}</p></div>
        </button>
        <button
          type="button"
          onClick={go("/admin?section=companies&enrolled=1")}
          className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 text-start cursor-pointer border-e border-border last:border-e-0 sm:border sm:rounded-2xl sm:border-border hover:border-primary/50 sm:hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="admin-overview-milestone-completion"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0"><Rocket className="h-4 w-4" /></span>
          <div className="min-w-0"><p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">{tr("adminOverview.milestonesCompleted")}</p><p className="font-mono text-base sm:text-lg" dir="ltr">{fmtNumber(p.milestones_done)} <span className="text-muted-foreground text-xs">/ {fmtNumber(p.milestones_total)}</span></p></div>
        </button>
      </div>
    </div>
  );
}

export default AdminOverview;
